import { Label } from "layout/typography/Label";
import { sortBy } from "lodash";
import { Button } from "primereact/button";
import { Chips, ChipsChangeParams } from "primereact/chips";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { MultiSelect } from "primereact/multiselect";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useGetPayersByAccountQuery } from "store/queries/payers";
import styled from "styled-components";

import { normaliseBenefitNameSynonyms } from "../../helpers/normaliseBenefitNameSynonyms";

const StyledMessage = styled(Message)`
  justify-content: flex-start;
`;

const tierOptions = [
  { name: "Not set — use the payer's default tier", value: "" },
  { name: "Tier 1 (preferred)", value: "Tier1" },
  { name: "Tier 2", value: "Tier2" },
];

const networkStatusOptions = [
  { name: "In network", value: "InNetwork" },
  { name: "Out of network", value: "OutOfNetwork" },
];

/**
 * CLE-2683 — how this account reads each payer's benefit lines.
 *
 * Payers routinely return several priced lines for one service, separated only by wording: an ASC
 * copay of $250 marked "FLEX PROVIDER" beside $500 marked "OTHER PLAN PROVIDER". Which one is this
 * facility's is a fact about its contract, so it is entered here rather than guessed at.
 *
 * Edit page only, like the Clear Fee rows below it: the payer list is scoped to the account and
 * there is no account to scope it to until one has been created.
 */
export function BenefitSelectionRules() {
  const { control } = useFormContext<any>();
  const { id: accountId } = useParams<{ id: string }>();

  const { data: payerByAccount, isLoading: loadingPayers } =
    useGetPayersByAccountQuery(accountId || "", { skip: !accountId });

  const payerOptions = sortBy(
    payerByAccount?.data?.map((payer: any) => ({
      label: payer.name,
      value: payer.id,
    })) || [],
    ["label"]
  );

  const { fields, append, remove } = useFieldArray({
    control,
    name: "benefitSelectionRules",
  });

  if (!accountId) {
    return (
      <StyledMessage
        severity="info"
        text="Benefit rules can be added once the account has been created and its payers are known."
      />
    );
  }

  return (
    <div className="flex flex-column gap-2">
      <Label htmlFor="benefitSelectionRules">Benefit Rules</Label>

      <StyledMessage
        severity="info"
        text="Keywords are matched against the wording on each benefit line, ignoring case, anywhere in the text — so use the distinctive part (FLEX PROVIDER, not NETWORK). Leave Payers empty for a rule that covers every payer, and Plans empty for one that covers every plan. Where two rules could apply, the more specific one wins."
      />

      {fields.map((field, index) => (
        <div
          key={field.id}
          className="formgrid grid mb-2 p-3 border-1 border-round surface-border"
        >
          <div className="field col-12 md:col-6">
            <Label htmlFor={`benefitSelectionRules.${index}.payerIds`}>
              Payers
            </Label>
            <Controller
              name={`benefitSelectionRules.${index}.payerIds`}
              control={control}
              render={({ field: { onChange, value } }) => (
                <MultiSelect
                  id={`benefitSelectionRules.${index}.payerIds`}
                  data-testid={`benefitSelectionRules.${index}.payerIds`}
                  className="w-full"
                  display="chip"
                  filter
                  options={payerOptions}
                  disabled={loadingPayers}
                  value={value ?? []}
                  placeholder="All payers"
                  onChange={(e) => onChange(e.value ?? [])}
                />
              )}
            />
          </div>

          <div className="field col-12 md:col-6">
            <Label htmlFor={`benefitSelectionRules.${index}.planKeywords`}>
              Plans / products (optional)
            </Label>
            <Controller
              name={`benefitSelectionRules.${index}.planKeywords`}
              control={control}
              render={({ field: { onChange, value } }) => (
                <Chips
                  id={`benefitSelectionRules.${index}.planKeywords`}
                  data-testid={`benefitSelectionRules.${index}.planKeywords`}
                  className="w-full"
                  value={value ?? []}
                  placeholder="CHOICE PLUS"
                  onChange={(e: ChipsChangeParams) =>
                    onChange(normaliseBenefitNameSynonyms(e.value ?? []))
                  }
                />
              )}
            />
          </div>

          <div className="field col-12 md:col-6">
            <Label htmlFor={`benefitSelectionRules.${index}.matchKeywords`}>
              Use lines containing
            </Label>
            <Controller
              name={`benefitSelectionRules.${index}.matchKeywords`}
              control={control}
              render={({ field: { onChange, value } }) => (
                <Chips
                  id={`benefitSelectionRules.${index}.matchKeywords`}
                  data-testid={`benefitSelectionRules.${index}.matchKeywords`}
                  className="w-full"
                  value={value ?? []}
                  placeholder="FLEX PROVIDER"
                  onChange={(e: ChipsChangeParams) =>
                    onChange(normaliseBenefitNameSynonyms(e.value ?? []))
                  }
                />
              )}
            />
          </div>

          <div className="field col-12 md:col-6">
            <Label htmlFor={`benefitSelectionRules.${index}.excludeKeywords`}>
              Never use lines containing
            </Label>
            <Controller
              name={`benefitSelectionRules.${index}.excludeKeywords`}
              control={control}
              render={({ field: { onChange, value } }) => (
                <Chips
                  id={`benefitSelectionRules.${index}.excludeKeywords`}
                  data-testid={`benefitSelectionRules.${index}.excludeKeywords`}
                  className="w-full"
                  value={value ?? []}
                  placeholder="OTHER PLAN PROVIDER"
                  onChange={(e: ChipsChangeParams) =>
                    onChange(normaliseBenefitNameSynonyms(e.value ?? []))
                  }
                />
              )}
            />
          </div>

          <div className="field col-12 md:col-4">
            <Label htmlFor={`benefitSelectionRules.${index}.tierPreference`}>
              Tier
            </Label>
            <Controller
              name={`benefitSelectionRules.${index}.tierPreference`}
              control={control}
              render={({ field: { onChange, value } }) => (
                <Dropdown
                  id={`benefitSelectionRules.${index}.tierPreference`}
                  data-testid={`benefitSelectionRules.${index}.tierPreference`}
                  className="w-full"
                  optionLabel="name"
                  options={tierOptions}
                  value={value ?? ""}
                  onChange={(e) => onChange(e.value === "" ? null : e.value)}
                />
              )}
            />
          </div>

          <div className="field col-12 md:col-4">
            <Label htmlFor={`benefitSelectionRules.${index}.networkStatus`}>
              Network status
            </Label>
            <Controller
              name={`benefitSelectionRules.${index}.networkStatus`}
              control={control}
              render={({ field: { onChange, value } }) => (
                <Dropdown
                  id={`benefitSelectionRules.${index}.networkStatus`}
                  data-testid={`benefitSelectionRules.${index}.networkStatus`}
                  className="w-full"
                  optionLabel="name"
                  options={networkStatusOptions}
                  value={value ?? "InNetwork"}
                  onChange={(e) => onChange(e.value)}
                />
              )}
            />
          </div>

          <div className="field col-12 md:col-4 flex align-items-end">
            <Button
              type="button"
              icon="pi pi-trash"
              label="Remove"
              className="p-button-outlined p-button-danger"
              data-testid={`benefitSelectionRules.${index}.remove`}
              onClick={() => remove(index)}
            />
          </div>
        </div>
      ))}

      <div>
        <Button
          type="button"
          icon="pi pi-plus"
          label="Add benefit rule"
          className="p-button-outlined"
          data-testid="addBenefitSelectionRule"
          onClick={() =>
            append({
              payerIds: [],
              planKeywords: [],
              matchKeywords: [],
              excludeKeywords: [],
              tierPreference: null,
              networkStatus: "InNetwork",
            })
          }
        />
      </div>
    </div>
  );
}
